/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — saved VIEW selection (People Rebuild edit-session
   grant, 2026-08-17). A "view" is a named bundle of DISPLAY
   preferences only (node style, team-box visibility, snap) —
   applying one is a pure prefs-setter call, never an edit-session
   action. Same localStorage-seed + server-wins-once pattern as every
   other org-chart pref (useOrgChartPrefs.ts), scoped to which view id
   is currently selected.

   `routing` is GONE from `OrgChartViewConfig` (People Rebuild
   orthogonal-lines law, 2026-08-18 — the line-style selector no
   longer exists). A saved view's `config` JSON on the server may
   still carry an old `routing` key from before this law — it is
   simply never read here, so it's silently ignored, not migrated;
   re-saving that view (Update) naturally drops it from then on.
   ═══════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { OrgChartView } from '../../../../../shared/entities';
import { Prefs } from '../../../../services/prefsService';
import { DEFAULT_NODE_STYLE, type NodeStylePrefs } from '../types';
import type { useOrgChartPrefs } from './useOrgChartPrefs';

const PREF_ACTIVE_VIEW_KEY = 'orgchart_activeView';
const ACTIVE_VIEW_LS_KEY = 'specforma:orgchart:activeView';

export interface OrgChartViewConfig {
  snap: boolean;
  teamBoxVisibility: Record<string, boolean>;
  nodeStyle: NodeStylePrefs;
}

function readStoredActiveViewId(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(ACTIVE_VIEW_LS_KEY) || null;
}

export function useOrgChartViewSelection(
  prefs: ReturnType<typeof useOrgChartPrefs>,
  views: OrgChartView[]
) {
  const [activeViewId, setActiveViewIdState] = useState<string | null>(readStoredActiveViewId);
  // Snapshot of the config we last APPLIED for the active view — compared
  // against the live prefs to show "(modified)" / drift.
  const appliedConfigRef = useRef<OrgChartViewConfig | null>(null);
  const [driftTick, setDriftTick] = useState(0);

  const persistActiveViewId = useCallback((id: string | null) => {
    setActiveViewIdState(id);
    try {
      if (id) window.localStorage.setItem(ACTIVE_VIEW_LS_KEY, id);
      else window.localStorage.removeItem(ACTIVE_VIEW_LS_KEY);
    } catch {
      /* best-effort */
    }
    Prefs.set(PREF_ACTIVE_VIEW_KEY, id);
  }, []);

  const currentConfig = useCallback(
    (): OrgChartViewConfig => ({
      snap: prefs.snapEnabled,
      teamBoxVisibility: prefs.visibleTeamIds,
      nodeStyle: prefs.nodeStyle,
    }),
    [prefs.snapEnabled, prefs.visibleTeamIds, prefs.nodeStyle]
  );

  const applyConfig = useCallback(
    (config: Partial<OrgChartViewConfig>) => {
      if (typeof config.snap === 'boolean') prefs.setSnapEnabled(config.snap);
      if (config.teamBoxVisibility) prefs.setTeamBoxVisibilityMap(config.teamBoxVisibility);
      if (config.nodeStyle) prefs.setNodeStyle({ ...DEFAULT_NODE_STYLE, ...config.nodeStyle });
    },
    [prefs]
  );

  const selectView = useCallback(
    (id: string | null) => {
      persistActiveViewId(id);
      if (!id) {
        appliedConfigRef.current = null;
        setDriftTick((v) => v + 1);
        return;
      }
      const view = views.find((v) => v.id === id);
      if (!view) return;
      const config = (view.config || {}) as Partial<OrgChartViewConfig>;
      applyConfig(config);
      appliedConfigRef.current = {
        snap: typeof config.snap === 'boolean' ? config.snap : prefs.snapEnabled,
        teamBoxVisibility: config.teamBoxVisibility || {},
        nodeStyle: { ...DEFAULT_NODE_STYLE, ...(config.nodeStyle || {}) },
      };
      setDriftTick((v) => v + 1);
    },
    [views, applyConfig, persistActiveViewId, prefs.snapEnabled]
  );

  const revert = useCallback(() => {
    if (!activeViewId) return;
    selectView(activeViewId);
  }, [activeViewId, selectView]);

  /** Called by the caller right after a successful Save/Update-view —
   *  resets the drift baseline to "current == applied" without
   *  re-running the setters (they're already current, that's what
   *  we're saving). */
  const markAppliedConfig = useCallback(
    (id: string, config: OrgChartViewConfig) => {
      persistActiveViewId(id);
      appliedConfigRef.current = config;
      setDriftTick((v) => v + 1);
    },
    [persistActiveViewId]
  );

  const clearActive = useCallback(() => {
    persistActiveViewId(null);
    appliedConfigRef.current = null;
    setDriftTick((v) => v + 1);
  }, [persistActiveViewId]);

  // Server-wins-once: once Prefs finishes loading, apply the SERVER copy
  // of the active-view id (if it differs from the localStorage seed) —
  // same pattern as useOrgChartPrefs's own appliedRef effect, kept
  // separate here because it also needs `views` to be loaded to apply
  // the view's config, not just note the id.
  const appliedServerRef = useRef(false);
  const [prefsTick, setPrefsTick] = useState(0);
  useEffect(() => Prefs.subscribe(() => setPrefsTick((v) => v + 1)), []);
  useEffect(() => {
    if (appliedServerRef.current || !Prefs.isReady()) return;
    const serverVal = Prefs.get(PREF_ACTIVE_VIEW_KEY);
    const serverId = typeof serverVal === 'string' && serverVal ? serverVal : null;
    if (serverId) persistActiveViewId(serverId);
    appliedServerRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsTick]);

  // Once we know which view id is active AND the views list has loaded,
  // apply that view's config exactly once (first time both are ready).
  const appliedInitialConfigRef = useRef(false);
  useEffect(() => {
    if (appliedInitialConfigRef.current) return;
    if (!activeViewId) return;
    if (views.length === 0) return;
    const view = views.find((v) => v.id === activeViewId);
    if (!view) return;
    appliedInitialConfigRef.current = true;
    const config = (view.config || {}) as Partial<OrgChartViewConfig>;
    applyConfig(config);
    appliedConfigRef.current = {
      snap: typeof config.snap === 'boolean' ? config.snap : prefs.snapEnabled,
      teamBoxVisibility: config.teamBoxVisibility || {},
      nodeStyle: { ...DEFAULT_NODE_STYLE, ...(config.nodeStyle || {}) },
    };
    setDriftTick((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeViewId, views]);

  const activeView = activeViewId ? views.find((v) => v.id === activeViewId) : undefined;
  const isModified = (() => {
    void driftTick;
    if (!activeViewId || !appliedConfigRef.current) return false;
    return JSON.stringify(currentConfig()) !== JSON.stringify(appliedConfigRef.current);
  })();

  return {
    activeViewId,
    activeView,
    currentConfig: currentConfig(),
    isModified,
    selectView,
    revert,
    markAppliedConfig,
    clearActive,
  };
}
